<template>
  <!-- This component's HTML is hidden for liveSessions - we only need the logic -->
  <div
    v-if="showContinueButton && !isLiveSession"
    class="interaction-continue-menu"
    :style="{ float: floatAlign }">
    <v-btn
      :id="'continue-btn-' + interaction.id"
      :small="sidebarStyle === 'portrait' || smallScreen"
      :aria-label="`${primaryText} button`"
      class="mr-0 v-btn-dropdown-primary interactionContinueButton"
      :class="{ 'jump-btn': jumpTime }"
      @click.prevent="primaryAction()"
      color="primary"
      :disabled="isContinueDisabled()">
        {{primaryText}}
    </v-btn>
    <v-tooltip top>
      <template v-slot:activator="{ on }">
        <v-btn
          v-if="!$_.isNil(jumpTime) && hasJumped"
          v-on="on"
          :small="sidebarStyle === 'portrait' || smallScreen"
          aria-label="show continue actions"
          flat
          icon
          outline
          color="primary"
          class="v-btn-dropdown"
          :class="{ 'mobile': mobile }"
          @click="triggerJump(jumpTime)">
          <v-icon>call_split</v-icon>
        </v-btn>
      </template>
      <span>{{jumpTooltip}}</span>
    </v-tooltip>
  </div>
</template>

<script>
/* global _ */

// Custom components:
import EventBus from '@/buses/EventBus';

// Plugins / Libraries:
import {
  mapGetters,
  mapActions,
  mapMutations,
  mapState
} from 'vuex';

export default {
  name: 'interaction-continue-menu',
  props: [
    'interactionId',
    'floatAlign',
    'visible',
  ],
  data() {
    return {
      menuProps: {
        "closeOnClick":true,
        "closeOnContentClick":true,
      },
      primaryActionFired: false,
      showMoreActions: false,
      hasJumped: _.get(this.isLiveSession, false),
    };
  },
  computed: {
    ...mapState('player', [
      'formatedDuration',
      'isLiveSession',
      'liveSessionTimeArray',
      'liveSessionUnixTime',
      'setFirstFocus',
    ]),
    ...mapState('interactions', [
      'interactionContinueButtonShouldBeHidden',
    ]),
    ...mapGetters('interactions', [
      'activeInteraction',
      'continueAction',
      'incompleteHotspots',
      'incompleteSidebar',
      'interactions',
      'nextDisabled',
    ]),
    ...mapGetters('player', [
      'bulbAttempt',
      'displayTime',
      'lookaround',
      'mobile',
      'sidebarStyle',
      'smallScreen',
      'user',
    ]),
    interaction() {
      return this.$store.getters['interactions/interaction'](this.interactionId) || {};
    },
    interactionAttempt() {
      return this.$store.getters['interactions/interactionAttempt'](this.interactionId) || {};
    },
    interactionOffsetCountdown() {
      if (!this.isLiveSession) return;
      const sessionInteraction = _.find(this.liveSessionTimeArray, { 'interactionId': this.interaction.id });
      return Math.trunc(sessionInteraction.unixDisplayOutTime - this.liveSessionUnixTime);
    },
    interactionMeta() {
      return this.$store.getters['interactions/interactionMeta'](this.interactionId) || {};
    },
    jumpTime() {
      if (['multiple_choice', 'poll_q'].includes(this.interaction.interactionType)) {
        let interactionOption = this.interaction.interactionOptions.find(io => io.id === this.interactionAttempt.interactionOptionIds[0]);
        if (interactionOption && _.isNumber(interactionOption.jump)) {
          return interactionOption.jump;
        }
      } else if (['check_all_apply', 'short_answer']) {
        if (this.interactionMeta.isAnswerCorrect && this.interaction.jumpCorrect) {
          return this.interaction.jumpCorrect;
        }
        else if (!this.interactionMeta.isAnswerCorrect && this.interaction.jumpIncorrect) {
          return this.interaction.jumpIncorrect;
        }
      }
      return null;
    },
    jumpTooltip() {
      return `Jump to ${this.formatedDuration(this.jumpTime)}`;
    },
    primaryText() {
      return this.continueAction({ interactionMeta: this.interactionMeta });
    },
    showContinueButton() {
      if (
        this.interactionContinueButtonShouldBeHidden
        || (
          this.interaction?.config?.preventBulbProgressionUntilInteractionCompletion
          && this.interaction.attempts !== 1
          && !this.interactionMeta.isAnswerCorrect
          && (
            this.interactionMeta.attempts < this.interaction.attempts
            || this.interaction.attempts === 0
          )
        )
      ) return false;
      if (this.interaction.interactionType !== 'pause_q') return true;
      if (
        this.interaction.interactionType === 'pause_q'
        && (this.interaction.displayInTime !== this.interaction.displayOutTime) // durational
        && !_.get(this, 'interaction.isRequired', 1)
        && !_.get(this, 'interaction.pause', 1)
      ) {
        return false;
      }
      if (
        this.interaction.displayInTime === this.interaction.displayOutTime // Single point in time
        || this.continueAction({ interactionMeta: this.interactionMeta }) === 'next' // Has a next interaction
        || this.displayTime - this.interaction.displayInTime < (this.lookaround * 4) // Show at start of duration if pause
      ) return true;
      return false;
    },
  },
  methods: {
    ...mapActions('interactions', [
      'continueOnward',
      'requestSetActiveInteraction',
    ]),
    ...mapMutations('player', [
      'setSnackbar',
      'triggerBulbCompleteTab',
    ]),
    ...mapMutations('interactions', [
      'updateInteractionRequired',
    ]),
    isContinueDisabled() {
      if (this.interactionMeta.isRetakeExplanation) {
        if (this.interactionMeta.isExplanationSubmitted) {
          return false;
        } else {
          if (this.interactionAttempt.explanation) {
            return (this.interactionAttempt.explanation.length > 0);
          } else {
           return false;
          }
        }
      } else {
        return (this.interactionMeta.isProcessingData);
      }
    },
    primaryAction() {
      if ( _.isNil(this.jumpTime) || this.hasJumped ) {
        // If it is the last interaction and the bulb is complete - open bulbCompleteTab
        if ( 
          this.interactionId === this.interactions[this.interactions.length - 1].id
          && this.bulbAttempt.complete
        ) {
          this.requestSetActiveInteraction(null);
          this.triggerBulbCompleteTab();
        } else {
          this.continueOnward({
            interactionMeta: this.interactionMeta,
            nextCallback: this.triggerNext,
            continueCallback: this.triggerContinue,
          });
        }
      }  else {
        this.triggerJump(this.jumpTime);
        this.setSnackbar({
					text:`Your response jumped you to ${this.formatedDuration(this.jumpTime)}`,
		  		color: 'warning',
				})
      }
    },
    triggerJump(displayTime) {
      this.hasJumped = true;
      EventBus.$emit('requestAction', {
        key: 'seek',
        seekTime: displayTime,
        bypassGates: ['notWithinBounds', 'alreadySeeking']
      });
    },
    triggerContinue() {
      this.hasJumped = true;
      this.interactionMeta.collapsed = true;
      document.getElementById('play-pause').focus();
      EventBus.$emit('requestAction', { key: 'play', setDesiredState: true });
    },
    triggerNext() {
      if (this.interactionMeta.overlay) this.interactionMeta.collapsed = true
      else {
        // go to the next in the order
        this.requestSetActiveInteraction('next');
      }
    },
  },
  mounted() {
    if (!this.isLiveSession) {
      const buttons = (this.$el.getElementsByTagName) ? this.$el.getElementsByTagName('button') : [];
      if (buttons.length > 0) buttons[0].focus();
    }
  },
  watch: {
    /*
      This watcher is used to watch the interactionOffsetCountdown and then trigger the Next/Continue logic
      when the timer reaches 0.  We first have to set the interaction to not be required, and then fire the
      primaryAction function which determines if it should go to the next interaction or continue playing
      the bulb.
    */
    'interactionOffsetCountdown' (n) {
      /*
        This logic takes the place of the primaryAction function for liveSessions
        If there is another interaction next in line does not trigger a play event
        Otherwise it triggers a play.  The activeInteraction is set by the sessionToDisplayTime
        function in the liveSessionTimeLoop
      */
      if (this.isLiveSession && n < 1 && !this.primaryActionFired) {
        this.primaryActionFired = true;
        this.updateInteractionRequired({
          interactionId: this.interactionId,
          isRequired: false,
        });
        if ( 
          this.interactionId === this.interactions[this.interactions.length - 1].id
          && this.bulbAttempt.complete
        ) {
          this.triggerBulbCompleteTab();
        } else if (
            !_.find(this.liveSessionTimeArray, (i) => {
              return Math.abs(i.unixDisplayInTime - this.liveSessionUnixTime) < 0.5;
            })
          ) {
          EventBus.$emit('requestAction', { key: 'play', setDesiredState: true });
        }
      }
    },
  }
};
</script>

<style scoped>
  .portrait .v-btn-dropdown-menu {
    top: -36px;
  }
  .jump-btn {
    border-radius: 2px 0 0 2px;
}
  .interaction-continue-menu {
    display: inline-flex;
  }

  .v-btn-dropdown {
    margin-left: 0px;
    border-radius: 0px;
    position: relative;
    box-shadow: 0 3px 1px -2px rgba(0,0,0,.2), 0 2px 2px 0 rgba(0,0,0,.14), 0 1px 5px 0 rgba(0,0,0,.12) !important;
  }
  .v-btn-dropdown.mobile {
    height: 28px;
  }

  .v-btn-dropdown-menu {
    position: absolute;
    right: 0;
    top: -48px;
  }
</style>
